import pluralize from 'pluralize';

import { type Board, type Clip, ClipType } from '@air/api/types';

import { GalleryItemType } from '~/components/Gallery/types';
import { type AssetDragItem, type BoardDragItem } from '~/components/Shared/Drag/dragTypes';
import { isAssetDragItem } from '~/components/Shared/Drag/utils/isAssetDragItem';
import { isBoardDragItem } from '~/components/Shared/Drag/utils/isBoardDragItem';
import {
  beginWorkspaceAgentComposerSelectionDrag,
  getWorkspaceAgentComposerSelectionDragSessionId,
  setWorkspaceAgentComposerSelectionDragAuthorizedItems,
} from '~/components/v2/components/workspace/workspace-agent-panel/drag/workspaceAgentComposerSelectionDragSession';
import { isPrivateBoard } from '~/store/centralizedBoard/utils';
import { type SelectableGalleryItem } from '~/store/selectedItems/types';

export const WORKSPACE_AGENT_COMPOSER_SELECTION_DROP_TYPE = 'application/x-air-workspace-agent-selection';

export const isWorkspaceAgentComposerSelectionDragDataTransfer = (dataTransfer: DataTransfer | null): boolean =>
  dataTransfer?.types.includes(WORKSPACE_AGENT_COMPOSER_SELECTION_DROP_TYPE) ?? false;

export type WorkspaceAgentComposerSelectionDragAsset = {
  kind: 'asset';
  assetId: string;
  versionId: string;
  title: string;
  importedName: string;
  ext: string;
  assetType: Clip['type'];
  thumbnailUrl: string | null;
};

export type WorkspaceAgentComposerSelectionDragBoard = {
  kind: 'board';
  boardId: string;
  title: string;
  coverImageUrl: string | null;
};

export type WorkspaceAgentComposerSelectionDragItem =
  | WorkspaceAgentComposerSelectionDragAsset
  | WorkspaceAgentComposerSelectionDragBoard;

/** Items-only shape from `buildWorkspaceAgentComposerSelectionDragPayload` (no session binding). */
export type WorkspaceAgentComposerSelectionDragPayloadItems = {
  items: WorkspaceAgentComposerSelectionDragItem[];
};

/** Wire format on `dataTransfer` — includes monotonic session id from dragstart. */
export type WorkspaceAgentComposerSelectionDragPayload = {
  sessionId: number;
  items: WorkspaceAgentComposerSelectionDragItem[];
};

export const workspaceAgentComposerSelectionDragItemAuthorizationKey = (
  item: WorkspaceAgentComposerSelectionDragItem,
): string => (item.kind === 'asset' ? `asset:${item.assetId}:${item.versionId}` : `board:${item.boardId}`);

export const filterWorkspaceAgentComposerSelectionDragItemsToAuthorizedSnapshot = (
  items: readonly WorkspaceAgentComposerSelectionDragItem[],
  authorizedKeys: ReadonlySet<string>,
): WorkspaceAgentComposerSelectionDragItem[] =>
  items.filter((item) => authorizedKeys.has(workspaceAgentComposerSelectionDragItemAuthorizationKey(item)));

const clipToDragAsset = (clip: Clip): WorkspaceAgentComposerSelectionDragAsset => ({
  assetId: clip.assetId,
  assetType: clip.type,
  ext: clip.ext,
  importedName: clip.importedName ?? '',
  kind: 'asset',
  thumbnailUrl: clip.assets.image || null,
  title: clip.title || clip.importedName || 'Untitled',
  versionId: clip.id,
});

const boardToDragBoard = (board: Board): WorkspaceAgentComposerSelectionDragBoard => ({
  boardId: board.id,
  coverImageUrl: board.coverImage?.url || board.thumbnails?.[0] || null,
  kind: 'board',
  title: board.title || 'Untitled board',
});

const selectableItemToDragItem = (selected: SelectableGalleryItem): WorkspaceAgentComposerSelectionDragItem | null => {
  if (selected.type === GalleryItemType.asset || selected.type === GalleryItemType.file) {
    return clipToDragAsset(selected.item);
  }

  if (selected.type === GalleryItemType.board && isPrivateBoard(selected.item)) {
    return boardToDragBoard(selected.item);
  }

  return null;
};

export type BuildWorkspaceAgentComposerSelectionDragPayloadParams = {
  draggedItem: BoardDragItem | AssetDragItem;
  isSelected: boolean;
  selectAll: boolean;
  selectedItems: readonly SelectableGalleryItem[];
};

export const buildWorkspaceAgentComposerSelectionDragPayload = ({
  draggedItem,
  isSelected,
  selectAll,
  selectedItems,
}: BuildWorkspaceAgentComposerSelectionDragPayloadParams): WorkspaceAgentComposerSelectionDragPayloadItems | null => {
  if (isSelected && selectAll) {
    return null;
  }

  if (isSelected) {
    const items = selectedItems
      .map(selectableItemToDragItem)
      .filter((entry): entry is WorkspaceAgentComposerSelectionDragItem => entry !== null);

    return items.length > 0 ? { items } : null;
  }

  if (isBoardDragItem(draggedItem)) {
    return { items: [boardToDragBoard(draggedItem.board)] };
  }

  if (isAssetDragItem(draggedItem)) {
    return { items: [clipToDragAsset(draggedItem.asset)] };
  }

  return null;
};

export const formatWorkspaceAgentComposerSelectionDragPreviewLabel = (
  payload: WorkspaceAgentComposerSelectionDragPayloadItems,
): string => {
  const [firstItem, ...rest] = payload.items;

  if (!firstItem) {
    return '';
  }

  if (rest.length === 0) {
    return firstItem.title;
  }

  return `${firstItem.title} and ${pluralize('other', rest.length, true)}`;
};

export const setWorkspaceAgentComposerSelectionDragData = (
  dataTransfer: DataTransfer,
  payload: WorkspaceAgentComposerSelectionDragPayloadItems | null,
): void => {
  if (!payload || payload.items.length === 0) {
    return;
  }

  beginWorkspaceAgentComposerSelectionDrag(formatWorkspaceAgentComposerSelectionDragPreviewLabel(payload));
  setWorkspaceAgentComposerSelectionDragAuthorizedItems(payload.items);
  const sessionId = getWorkspaceAgentComposerSelectionDragSessionId();
  const wirePayload: WorkspaceAgentComposerSelectionDragPayload = { items: payload.items, sessionId };
  dataTransfer.setData(WORKSPACE_AGENT_COMPOSER_SELECTION_DROP_TYPE, JSON.stringify(wirePayload));
};

const CLIP_TYPE_VALUES: readonly string[] = Object.values(ClipType);

const isUnknownRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isClipType = (value: unknown): value is Clip['type'] =>
  typeof value === 'string' && CLIP_TYPE_VALUES.includes(value);

const parseDragAssetEntry = (entry: Record<string, unknown>): WorkspaceAgentComposerSelectionDragAsset | null => {
  if (entry.kind !== 'asset') {
    return null;
  }

  const { assetId, versionId } = entry;

  if (typeof assetId !== 'string' || typeof versionId !== 'string') {
    return null;
  }

  const assetType = entry.assetType;

  return {
    assetId,
    assetType: isClipType(assetType) ? assetType : ClipType.nonMedia,
    ext: typeof entry.ext === 'string' ? entry.ext : '',
    importedName: typeof entry.importedName === 'string' ? entry.importedName : '',
    kind: 'asset',
    thumbnailUrl: typeof entry.thumbnailUrl === 'string' ? entry.thumbnailUrl : null,
    title: typeof entry.title === 'string' ? entry.title : 'Untitled',
    versionId,
  };
};

const parseDragBoardEntry = (entry: Record<string, unknown>): WorkspaceAgentComposerSelectionDragBoard | null => {
  if (entry.kind !== 'board') {
    return null;
  }

  const { boardId } = entry;

  if (typeof boardId !== 'string') {
    return null;
  }

  return {
    boardId,
    coverImageUrl: typeof entry.coverImageUrl === 'string' ? entry.coverImageUrl : null,
    kind: 'board',
    title: typeof entry.title === 'string' ? entry.title : 'Untitled board',
  };
};

export const parseWorkspaceAgentComposerSelectionDragPayload = (
  raw: string,
): WorkspaceAgentComposerSelectionDragPayload | null => {
  try {
    const parsed: unknown = JSON.parse(raw);

    if (!isUnknownRecord(parsed) || !('items' in parsed) || !('sessionId' in parsed)) {
      return null;
    }

    const sessionId = parsed.sessionId;

    if (typeof sessionId !== 'number' || !Number.isInteger(sessionId)) {
      return null;
    }

    const items = parsed.items;

    if (!Array.isArray(items)) {
      return null;
    }

    const normalized: WorkspaceAgentComposerSelectionDragItem[] = [];

    for (const entry of items) {
      if (!isUnknownRecord(entry)) {
        continue;
      }

      const asset = parseDragAssetEntry(entry);

      if (asset) {
        normalized.push(asset);
        continue;
      }

      const board = parseDragBoardEntry(entry);

      if (board) {
        normalized.push(board);
      }
    }

    return normalized.length > 0 ? { items: normalized, sessionId } : null;
  } catch {
    return null;
  }
};
